import { ref } from "vue";

// Global state to prevent multiple simultaneous imports
let isLoading = false;
let loadPromise: Promise<void> | null = null;

// Global refs shared across all instances
const gsap = ref();
const ScrollTrigger = ref();
const ScrollSmoother = ref();
const ScrollToPlugin = ref();
const isLoaded = ref(false);

export const useGSAP = () => {
  const loadGSAP = async () => {
    if (process.server) return;

    // If already loaded, return immediately
    if (isLoaded.value) return;

    // If currently loading, wait for the existing promise
    if (isLoading && loadPromise) {
      await loadPromise;
      return;
    }

    // Start loading
    isLoading = true;
    loadPromise = (async () => {
      try {
        console.log("Loading GSAP modules...");

        // Mobile-specific: Add delay for Android Chrome (client-side only)
        if (process.client) {
          const isMobile =
            /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent,
            );
          const isAndroidChrome = /Android.*Chrome/i.test(navigator.userAgent);

          if (isAndroidChrome) {
            // Add small delay for Android Chrome to prevent race conditions
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        // Import modules sequentially to avoid race conditions on mobile
        const gsapModule = await import("gsap").catch((err) => {
          console.error("Failed to import gsap:", err);
          throw new Error(`GSAP import failed: ${err.message}`);
        });

        const scrollTriggerModule = await import("gsap/ScrollTrigger").catch(
          (err) => {
            console.error("Failed to import ScrollTrigger:", err);
            throw new Error(`ScrollTrigger import failed: ${err.message}`);
          },
        );

        const scrollSmootherModule = await import("gsap/ScrollSmoother").catch(
          (err) => {
            console.error("Failed to import ScrollSmoother:", err);
            throw new Error(`ScrollSmoother import failed: ${err.message}`);
          },
        );

        const scrollToPluginModule = await import("gsap/ScrollToPlugin").catch(
          (err) => {
            console.error("Failed to import ScrollToPlugin:", err);
            throw new Error(`ScrollToPlugin import failed: ${err.message}`);
          },
        );

        // Assign to refs
        gsap.value = gsapModule.gsap || gsapModule.default;
        ScrollTrigger.value =
          scrollTriggerModule.ScrollTrigger || scrollTriggerModule.default;
        ScrollSmoother.value =
          scrollSmootherModule.ScrollSmoother || scrollSmootherModule.default;
        ScrollToPlugin.value =
          scrollToPluginModule.ScrollToPlugin || scrollToPluginModule.default;

        // Validate that modules loaded correctly
        if (!gsap.value) throw new Error("GSAP main module failed to load");
        if (!ScrollTrigger.value)
          throw new Error("ScrollTrigger plugin failed to load");
        if (!ScrollSmoother.value)
          throw new Error("ScrollSmoother plugin failed to load");
        if (!ScrollToPlugin.value)
          throw new Error("ScrollToPlugin plugin failed to load");

        // Register plugins
        gsap.value.registerPlugin(
          ScrollTrigger.value,
          ScrollSmoother.value,
          ScrollToPlugin.value,
        );

        isLoaded.value = true;
        console.log("GSAP modules loaded successfully");
      } catch (error) {
        console.error("Failed to load GSAP:", error);
        isLoaded.value = false;
        throw error;
      } finally {
        isLoading = false;
        loadPromise = null;
      }
    })();

    await loadPromise;
  };

  // Utility function to safely use GSAP
  const safeGSAP = async (callback: () => void) => {
    try {
      await loadGSAP();
      if (isLoaded.value && gsap.value) {
        callback();
      }
    } catch (error) {
      console.warn("GSAP operation failed:", error);
    }
  };

  return {
    gsap,
    ScrollTrigger,
    ScrollSmoother,
    ScrollToPlugin,
    isLoaded,
    loadGSAP,
    safeGSAP,
  };
};
